import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { ArrowLeft, Printer, Spinner } from "@phosphor-icons/react";

/** Printable SKU sheet for Avery 2-labels-per-page shipping sheets (5.5" x 8.5" each).
 *
 * Layout:
 *   8.5" × 11" page → top half (5" tall) + 0.5" center gutter + bottom half (5" tall)
 *   Inside each half, a grid of SKU cells the user will cut into individual stickers.
 *
 * The center gutter sits exactly on Avery's cut line so no sticker ever gets sliced
 * in half. Each cell has dashed cut-guide borders.
 */

// A cell is ~1.5" × 0.65" — large enough for Claude Vision OCR to read reliably,
// small enough to fit a lot of SKUs per page. User can adjust via settings.
const DEFAULTS = {
  cols: 5,        // columns per half
  rows: 7,        // rows per half  → 35 stickers per half, 70 per sheet
};

/** Pick the label text we'll print for each listing.
 *  Falls back from custom_label → sku → listing_id so every item shows something. */
const labelFor = (r) => (r.custom_label || r.sku || r.listing_id || "").trim();

export default function PrintSkuPage() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cols, setCols] = useState(DEFAULTS.cols);
  const [gridRows, setGridRows] = useState(DEFAULTS.rows);
  const [onlyMissing, setOnlyMissing] = useState(false); // only print items whose custom_label was never set by the user
  const [sortBy, setSortBy] = useState("date"); // "date" | "title" | "sku"
  const [error, setError] = useState("");

  useEffect(() => {
    api.get("/ebay/inventory")
      .then(({ data }) => setRows(Array.isArray(data) ? data : []))
      .catch((err) => setError(err?.response?.data?.detail || "Failed to load inventory"))
      .finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => {
    const src = rows.filter((r) => labelFor(r));
    const maybeFiltered = !onlyMissing ? src : src.filter((r) => {
      const cl = (r.custom_label || "").trim();
      const sku = (r.sku || "").trim();
      const isAutoGenerated = !cl || /^[a-f0-9]{8,12}$/i.test(cl);
      const skuIsAutoGenerated = /^[a-f0-9]{8,12}$/i.test(sku);
      return isAutoGenerated && skuIsAutoGenerated;
    });
    // Apply sort — stable, case-insensitive
    const copy = [...maybeFiltered];
    if (sortBy === "title") {
      copy.sort((a, b) => (a.title || "").toLowerCase().localeCompare((b.title || "").toLowerCase()));
    } else if (sortBy === "sku") {
      copy.sort((a, b) => labelFor(a).toLowerCase().localeCompare(labelFor(b).toLowerCase()));
    } else {
      // date — newest first (last_modified is ISO string; string compare works for ISO)
      copy.sort((a, b) => String(b.last_modified || "").localeCompare(String(a.last_modified || "")));
    }
    return copy;
  }, [rows, onlyMissing, sortBy]);

  const perHalf = cols * gridRows;
  const perSheet = perHalf * 2;

  // Split into pages. Each page has topHalf + bottomHalf.
  const pages = useMemo(() => {
    const out = [];
    for (let i = 0; i < filtered.length; i += perSheet) {
      const slice = filtered.slice(i, i + perSheet);
      out.push({
        top: slice.slice(0, perHalf),
        bottom: slice.slice(perHalf, perSheet),
      });
    }
    return out;
  }, [filtered, perHalf, perSheet]);

  return (
    <div className="min-h-screen bg-neutral-200 print:bg-white">
      {/* Print-only CSS */}
      <style>{`
        @page { size: 8.5in 11in; margin: 0; }
        @media print {
          html, body { background: white !important; }
          .no-print { display: none !important; }
          .print-sheet { page-break-after: always; box-shadow: none !important; margin: 0 !important; }
          .print-sheet:last-child { page-break-after: auto; }
        }
      `}</style>

      {/* Toolbar — screen only */}
      <div className="no-print sticky top-0 z-30 bg-white border-b-4 border-black">
        <div className="max-w-[8.5in] mx-auto px-6 py-3 flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3">
            <Link to="/inventory" className="border-2 border-black p-2 active:translate-y-[1px]" data-testid="print-sku-back-btn">
              <ArrowLeft size={18} weight="bold" />
            </Link>
            <h1 className="font-heading text-xl font-black uppercase tracking-tighter">Print SKU Labels</h1>
            <span className="font-body text-xs text-neutral-500" data-testid="print-sku-count">
              {loading ? "…" : `${filtered.length} label${filtered.length === 1 ? "" : "s"} · ${pages.length} sheet${pages.length === 1 ? "" : "s"}`}
            </span>
          </div>
          <div className="flex items-center gap-3 text-xs">
            <label className="flex items-center gap-1.5 font-body">
              Cols
              <input
                type="number"
                min={2}
                max={8}
                value={cols}
                onChange={(e) => setCols(Math.max(2, Math.min(8, parseInt(e.target.value) || DEFAULTS.cols)))}
                className="w-12 p-1 border-2 border-black font-mono text-center"
                data-testid="print-sku-cols"
              />
            </label>
            <label className="flex items-center gap-1.5 font-body">
              Rows
              <input
                type="number"
                min={3}
                max={12}
                value={gridRows}
                onChange={(e) => setGridRows(Math.max(3, Math.min(12, parseInt(e.target.value) || DEFAULTS.rows)))}
                className="w-12 p-1 border-2 border-black font-mono text-center"
                data-testid="print-sku-rows"
              />
            </label>
            <label className="flex items-center gap-1.5 font-body">
              Sort
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="p-1 border-2 border-black font-body text-xs bg-white"
                data-testid="print-sku-sort"
              >
                <option value="date">Date added</option>
                <option value="title">Item title</option>
                <option value="sku">SKU</option>
              </select>
            </label>
            <label className="flex items-center gap-1.5 font-body">
              <input
                type="checkbox"
                checked={onlyMissing}
                onChange={(e) => setOnlyMissing(e.target.checked)}
                className="w-4 h-4 border-2 border-black accent-black"
                data-testid="print-sku-only-missing"
              />
              Only unlabeled
            </label>
            <button
              onClick={() => window.print()}
              disabled={loading || filtered.length === 0}
              className="border-2 border-black bg-black text-white px-3 py-1.5 font-heading text-xs font-bold uppercase flex items-center gap-1.5 disabled:opacity-40 active:translate-y-[1px]"
              data-testid="print-sku-print-btn"
            >
              <Printer size={14} weight="bold" /> Print
            </button>
          </div>
        </div>
        <div className="max-w-[8.5in] mx-auto px-6 pb-3 font-body text-[11px] text-neutral-500">
          Prints on Avery 2-label shipping sheets (8.5&quot; × 11&quot;, 2 labels per page). The center gutter avoids the cut line — cut the sheet in half, then cut each half into individual stickers.
        </div>
      </div>

      {/* Body */}
      {loading ? (
        <div className="p-12 text-center no-print">
          <Spinner size={28} weight="bold" className="animate-spin mx-auto text-neutral-500" />
        </div>
      ) : error ? (
        <div className="p-12 text-center font-body text-sm text-red-600 no-print" data-testid="print-sku-error">{error}</div>
      ) : filtered.length === 0 ? (
        <div className="p-12 text-center font-body text-sm text-neutral-600 no-print" data-testid="print-sku-empty">
          No SKUs to print.
        </div>
      ) : (
        <div className="py-6 flex flex-col items-center gap-6 print:p-0 print:gap-0">
          {pages.map((page, idx) => (
            <Sheet key={idx} page={page} cols={cols} gridRows={gridRows} />
          ))}
        </div>
      )}
    </div>
  );
}

/** One printable 8.5" × 11" sheet. */
function Sheet({ page, cols, gridRows }) {
  return (
    <div
      className="print-sheet relative bg-white shadow-[4px_4px_0px_0px_rgba(0,0,0,0.2)]"
      style={{ width: "8.5in", height: "11in", padding: "0.25in" }}
      data-testid="print-sku-sheet"
    >
      {/* Top half (Avery label 1) */}
      <Half cells={page.top} cols={cols} rows={gridRows} position="top" />

      {/* Center gutter — sits exactly on the Avery cut line, 0.5in */}
      <div
        className="relative border-t border-b border-dashed border-neutral-400 flex items-center justify-center"
        style={{ height: "0.5in" }}
      >
        <span className="font-heading text-[8px] uppercase tracking-widest text-neutral-400">Cut here</span>
      </div>

      {/* Bottom half (Avery label 2) */}
      <Half cells={page.bottom} cols={cols} rows={gridRows} position="bottom" />
    </div>
  );
}

/** One half-sheet grid of SKU cells. */
function Half({ cells, cols, rows }) {
  // Fill empty slots so the grid stays aligned
  const total = cols * rows;
  const filled = [...cells];
  while (filled.length < total) filled.push(null);

  return (
    <div
      className="grid"
      style={{
        height: "5in",
        gridTemplateColumns: `repeat(${cols}, 1fr)`,
        gridTemplateRows: `repeat(${rows}, 1fr)`,
        gap: "0",
      }}
    >
      {filled.map((cell, i) => (
        <SkuCell key={i} row={cell} />
      ))}
    </div>
  );
}

/** A single SKU sticker cell with dashed cut-guide borders.
 *  Two lines: SKU on top (large bold), item title below in IBM Plex Sans 7pt. */
function SkuCell({ row }) {
  if (!row) {
    return <div className="border border-dashed border-neutral-200" />;
  }
  const label = labelFor(row);
  const title = (row.title || "").slice(0, 100);
  // Dynamically size SKU: long labels shrink so they fit where possible.
  const skuFontSize = label.length > 18 ? "10pt" : label.length > 12 ? "13pt" : label.length > 7 ? "18pt" : "22pt";

  return (
    <div
      className="border border-dashed border-neutral-300 flex flex-col items-center justify-center overflow-hidden px-1 py-1"
      data-testid={`print-sku-cell-${label}`}
    >
      <span
        className="font-heading font-black tracking-tight text-center text-black break-all"
        style={{ fontSize: skuFontSize, lineHeight: 1.05 }}
      >
        {label}
      </span>
      {title && (
        <span
          className="text-center text-neutral-700 leading-tight mt-0.5 overflow-hidden"
          style={{
            fontFamily: '"IBM Plex Sans", system-ui, sans-serif',
            fontSize: "7pt",
            lineHeight: 1.15,
            display: "-webkit-box",
            WebkitLineClamp: 3,
            WebkitBoxOrient: "vertical",
          }}
          data-testid={`print-sku-cell-title-${label}`}
        >
          {title}
        </span>
      )}
    </div>
  );
}
